import { PropsWithChildren, Suspense, useCallback, useMemo } from "react"
import { Dialog, Stack } from "@mui/material"
import { ErrorBoundaryModal } from "../../errors/ErrorBoundaryModal"
import { SafeArea } from "../../skeleton/SafeArea"
import {
    QueryErrorBoundary,
    QueryErrorFallback,
    QueryErrorFallbackProps,
    QueryFallbackMessage,
} from "../../../../core/api/components/QueryErrorBoundary"
import { CreateOrderDialogLoading } from "../mainForm/CreateOrderDialogLoading"
import { FallbackProps } from "react-error-boundary"
import { CreateOrderDialogTitle } from "./CreateOrderDialogTitle"
import { makeStyles } from "tss-react/mui"
import { ErrorMatcher } from "../../../../core/api/errorTypes"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { CreateOrderDialogFlow } from "../createOrderTypes"

const useStyles = makeStyles()((theme) => ({
    fallbackContent: {
        height: "100%",
    },
}))

type CreateOrderFallbackProps = QueryErrorFallbackProps &
    Readonly<{
        onClose: () => void
        flowConfig?: CreateOrderDialogFlow
    }>

function CreateOrderFallback({ onClose, flowConfig, ...rest }: CreateOrderFallbackProps) {
    const { classes } = useStyles()

    return (
        <Stack direction="column" flexWrap="nowrap" className={classes.fallbackContent}>
            <CreateOrderDialogTitle handleClose={onClose} flowConfig={flowConfig}></CreateOrderDialogTitle>
            <QueryErrorFallback showError {...rest} />
        </Stack>
    )
}

type CreateOrderDialogWrapProps = Readonly<
    PropsWithChildren<{
        onBehalfOf: boolean
        open: boolean
        onClose: () => void
        flowConfig?: CreateOrderDialogFlow
    }>
>

export function CreateOrderDialogWrap({ onBehalfOf, open, onClose, children, flowConfig }: CreateOrderDialogWrapProps) {
    const { l10n } = useAppLocalization()
    const errorMatchers = useMemo(() => {
        const onBehalfOfMatchers: ErrorMatcher[] = [
            {
                type: "response-status",
                match: 403,
                skipDefaultAction: true,
                message: l10n.getString("create-order-access-denied-on-behalf-of"),
            },
        ]

        return onBehalfOf ? onBehalfOfMatchers : []
    }, [l10n])

    const CustomFallback = useCallback(
        (props: QueryErrorFallbackProps) => CreateOrderFallback({ onClose, flowConfig, ...props }),
        [onClose],
    )

    return (
        <Dialog open={open} onClose={onClose} fullWidth fullScreen scroll="paper">
            <ErrorBoundaryModal onClose={onClose}>
                <SafeArea>
                    <QueryErrorBoundary Fallback={CustomFallback} errorMatchers={errorMatchers}>
                        <Suspense fallback={<CreateOrderDialogLoading handleClose={onClose} flowConfig={flowConfig} />}>
                            {children}
                        </Suspense>
                    </QueryErrorBoundary>
                </SafeArea>
            </ErrorBoundaryModal>
        </Dialog>
    )
}
